import React, { useState } from 'react'
import { Card, Form, Button, Row, Col, DatePicker, Spin, Table } from 'antd'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { lzDownloadBlobFile, wtApiCall } from 'src/helpers/commons/ApiWrapper'
import {
  getExcelDateExportConfig,
  useMasterDateTimeFormats,
} from 'src/helpers/commons/masterDateFormat'
import moment from 'moment'

const BranchTrackingReport = () => {
  const { dateFormat, formatDate, formatDateTime } = useMasterDateTimeFormats()
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm()
  const [reportData, setReportData] = useState([])
  const branches = wtGetFromLocalStorage('branches') || []
  const [dateRange, setDateRange] = useState([])
  const [fromBranchId, setFromBranchId] = useState(0)
  const [fromBranchName, setFromBranchName] = useState('')
  const [toBranchId, setToBranchId] = useState(0)
  const [toBranchName, setToBranchName] = useState('')

  const changeValues = (key, val) => {
    setReportData([])

    if (key === 'date_range') {
      setDateRange(val || [])
    }

    if (key === 'from_branch_id') {
      setFromBranchId(val?.value || 0)
      setFromBranchName(val?.label || '')
    }

    if (key === 'to_branch_id') {
      setToBranchId(val?.value || 0)
      setToBranchName(val?.label || '')
    }
  }

  const getReportData = () => {
    if (!dateRange || dateRange.length < 2) {
      lzNotifyErrorMessageFromResult('Please select effective date range')
      return
    }

    setIsLoading(true)
    wtApiCall(
      'reports/branch_tracking_report',
      {
        from_date: dateRange[0].format('YYYY-MM-DD'),
        to_date: dateRange[1].format('YYYY-MM-DD'),
        from_branch_id: fromBranchId,
        to_branch_id: toBranchId,
      },
      'get',
      (response) => {
        setReportData(response.data || [])
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  const escapeExcelValue = (value) => {
    if (value === null || typeof value === 'undefined' || value === '') {
      return '-'
    }

    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  const buildExcelHtml = () => {
    const generatedAt = formatDateTime(moment(), moment().format('YYYY-MM-DD HH:mm:ss'))
    const dateLabel =
      dateRange && dateRange.length === 2
        ? `${formatDate(dateRange[0], '-')} to ${formatDate(dateRange[1], '-')}`
        : '-'

    const rows = reportData
      .map((row, index) => {
        const joiningDateExport = getExcelDateExportConfig(row.date_of_joining, dateFormat, '-')
        const effectiveDateExport = getExcelDateExportConfig(row.effective_date, dateFormat, '-')

        const getExcelDateCellHtml = (exportConfig) => {
          if (exportConfig.type === 'empty') {
            return '<td></td>'
          }

          if (exportConfig.type === 'str') {
            return `<td x:str>${escapeExcelValue(exportConfig.value)}</td>`
          }

          return `<td style="mso-number-format:'${exportConfig.numberFormat}';" x:num>${exportConfig.value}</td>`
        }

        return `
          <tr>
            <td>${index + 1}</td>
            <td>${escapeExcelValue(row.employee_id)}</td>
            <td>${escapeExcelValue(row.pl_number)}</td>
            <td>${escapeExcelValue(row.employee_name)}</td>
            <td>${escapeExcelValue(row.designation)}</td>
            ${getExcelDateCellHtml(joiningDateExport)}
            <td>${escapeExcelValue(row.from_branch)}</td>
            <td>${escapeExcelValue(row.to_branch)}</td>
            <td>${escapeExcelValue(row.from_department)}</td>
            <td>${escapeExcelValue(row.to_department)}</td>
            ${getExcelDateCellHtml(effectiveDateExport)}
            <td>${escapeExcelValue(row.remarks)}</td>
          </tr>
        `
      })
      .join('')

    return `
      <html xmlns:o="urn:schemas-microsoft-com:office:office"
        xmlns:x="urn:schemas-microsoft-com:office:excel"
        xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
          <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Branch Tracking</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
          <style>
            body{font-family:Calibri,Arial,sans-serif;font-size:11pt;color:#000;}
            table{border-collapse:collapse;width:100%;}
            td,th{border:1px solid #cfd8e3;padding:6px 8px;vertical-align:middle;}
            .title{font-size:18pt;font-weight:700;text-align:center;border:none;padding:10px 0;}
            .meta{font-style:italic;text-align:center;border:none;color:#44546A;padding:4px 0 8px 0;}
            .header{background:#1F4E78;color:#fff;font-weight:700;text-align:center;}
          </style>
        </head>
        <body>
          <table>
            <tr><td class="title" colspan="12">Branch Tracking Report</td></tr>
            <tr><td class="meta" colspan="12">Effective Date Range: ${escapeExcelValue(dateLabel)}</td></tr>
            <tr><td class="meta" colspan="12">Generated at: ${escapeExcelValue(generatedAt)}</td></tr>
            <tr>
              <th class="header">Sr.</th>
              <th class="header">Emp ID</th>
              <th class="header">PL Number</th>
              <th class="header">Name</th>
              <th class="header">Designation</th>
              <th class="header">Date of Joining</th>
              <th class="header">From Branch</th>
              <th class="header">To Branch</th>
              <th class="header">From Department</th>
              <th class="header">To Department</th>
              <th class="header">Effective Date</th>
              <th class="header">Remarks</th>
            </tr>
            ${rows}
          </table>
        </body>
      </html>
    `
  }

  const exportToExcel = async () => {
    if (!dateRange || dateRange.length < 2) {
      lzNotifyErrorMessageFromResult('Please select effective date range')
      return
    }

    setIsLoading(true)
    try {
      const excelBlob = new Blob([buildExcelHtml()], {
        type: 'application/vnd.ms-excel;charset=utf-8;',
      })
      lzDownloadBlobFile(
        excelBlob,
        `BranchTrackingReport_${moment().format('YYYY_MM_DD_HH_mm_ss')}.xls`,
      )
    } finally {
      setIsLoading(false)
    }
  }

  const columns = [
    {
      key: 'sr',
      title: 'Sr.',
      render: (_, __, index) => index + 1,
      width: 70,
      fixed: 'left',
    },
    {
      key: 'employee_id',
      title: 'Emp ID',
      dataIndex: 'employee_id',
      width: 100,
    },
    {
      key: 'pl_number',
      title: 'PL Number',
      dataIndex: 'pl_number',
      width: 120,
      render: (text) => text || '-',
    },
    {
      key: 'employee_name',
      title: 'Name',
      dataIndex: 'employee_name',
      width: 220,
      render: (text, record) => (
        <a href={`/employee-profile?id=${record.employee_id}`} target="_blank" rel="noreferrer">
          {text || '-'}
        </a>
      ),
    },
    {
      key: 'designation',
      title: 'Designation',
      dataIndex: 'designation',
      width: 180,
      render: (text) => text || '-',
    },
    {
      key: 'date_of_joining',
      title: 'Date of Joining',
      dataIndex: 'date_of_joining',
      width: 140,
      render: (text) => formatDate(text, '-'),
    },
    {
      key: 'from_branch',
      title: 'From Branch',
      dataIndex: 'from_branch',
      width: 180,
      render: (text) => text || '-',
    },
    {
      key: 'to_branch',
      title: 'To Branch',
      dataIndex: 'to_branch',
      width: 180,
      render: (text) => text || '-',
    },
    {
      key: 'from_department',
      title: 'From Department',
      dataIndex: 'from_department',
      width: 180,
      render: (text) => text || '-',
    },
    {
      key: 'to_department',
      title: 'To Department',
      dataIndex: 'to_department',
      width: 180,
      render: (text) => text || '-',
    },
    {
      key: 'effective_date',
      title: 'Effective Date',
      dataIndex: 'effective_date',
      width: 140,
      render: (text) => formatDate(text, '-'),
    },
    {
      key: 'remarks',
      title: 'Remarks',
      dataIndex: 'remarks',
      width: 240,
      render: (text) => text || '-',
    },
  ]

  return (
    <div
      className="container-fluid"
      style={{
        padding: '1em',
        backgroundColor: 'white',
        minHeight: '100vh',
        fontFamily: 'Helvetica',
      }}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={getReportData}
        style={{ maxWidth: '100%', margin: '0 auto' }}
      >
        <Card
          style={{ width: '100%' }}
          title={<span style={{ fontSize: '26px', marginLeft: '40%' }}>Branch Tracking Report</span>}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12} md={6}>
              <label>Effective Date Range: *</label>
              <DatePicker.RangePicker
                onChange={(value) => changeValues('date_range', value)}
                style={{ width: '100%', height: 35 }}
                value={dateRange}
              />
            </Col>

            <Col xs={24} sm={12} md={6}>
              <label>From Branch:</label>
              <WidgetBranchSelector
                value={fromBranchId}
                displayValue={fromBranchName}
                branches={branches}
                placeholder="Select From Branch"
                onChange={(values) => changeValues('from_branch_id', values)}
                showCloudIcon={false}
              />
            </Col>

            <Col xs={24} sm={12} md={6}>
              <label>To Branch:</label>
              <WidgetBranchSelector
                value={toBranchId}
                displayValue={toBranchName}
                branches={branches}
                placeholder="Select To Branch"
                onChange={(values) => changeValues('to_branch_id', values)}
                showCloudIcon={false}
              />
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ marginTop: 30, display: 'flex', gap: 8 }}>
                <Button type="primary" htmlType="submit" disabled={isLoading}>
                  Search
                </Button>
                <Button
                  style={{ backgroundColor: '#4BB543', color: '#fff' }}
                  onClick={exportToExcel}
                  disabled={isLoading || reportData.length === 0}
                >
                  Export
                </Button>
              </div>
            </Col>
          </Row>
        </Card>
      </Form>

      <Card style={{ marginTop: 20 }}>
        <Spin spinning={isLoading}>
          <Table
            columns={columns}
            dataSource={reportData}
            rowKey="id"
            pagination={false}
            bordered
            scroll={{ x: 1900, y: 400 }}
          />
        </Spin>
      </Card>
    </div>
  )
}

export default BranchTrackingReport
